<template>
  <div>
    <button
      v-if="!courseTest"
      class="font-bold"
      @click="determineReturn()"
    >
      <span><t-icon
        class="text-text-secondary mr-2"
        name="arrow-left"
      /></span> Back to Search Results
    </button>
    <div
      v-if="!courseTest"
      class="p-2"
    >
      <h1
        class="text-xl"
        v-html="selectedSession.title"
      />
      <div
        v-for="instructor in selectedSession.instructors"
        :key="instructor.id"
      >
        <a
          v-if="instructor.url"
          :href="`https://summit-education.com/instructor/${instructor.url}`"
          target="_blank"
        >
          <h6 class="text-base">
            {{ instructor.name }}
          </h6>
        </a>
        <h6
          v-else
          class="text-base"
        >
          {{ instructor.name }}
        </h6>
      </div>
    </div>
    <div
      v-if="!courseTest"
      class="flex pt-4 pl-0 md:pl-2"
    >
      <course-thumbnail
        :master-course-id="masterCourseId"
        :thumbnail="thumbnail"
        :preview-id="previewId"
        :podbean-id="selectedSession.podbeanId"
        :title="courseTitle"
      />
    </div>
    <div v-if="selectedFormat === 'PODCAST'">
      <podcast-social-icons />
    </div>
    <div
      v-if="sessionCount > 0"
      class="md:hidden"
    >
      <course-ce-validator
        :brochure-deadline="selectedSession.brochureDeadlineDate"
        :is-zero-hour="selectedSession.credits === 0"
        :session="selectedSession"
        :user-state="userState"
        :user-profession="userProfession"
        :ce-info="ceInfo"
        :asha-review="ashaReview"
        :format="selectedFormat"
        @update="updateCeInfo"
        @show-ce-tab="toggleCeTab"
        @scroll-to-tabs="scrollToTab('#info')"
      />
      <div v-if="!isBrochure">
        <div
          v-if="certSeries.length > 0"
          class="pb-4"
        >
          <h5 class="leading-loose">
            Certification Series
          </h5>
          <div
            v-for="c in certSeries"
            :key="c.id"
            class="grid grid-flow-col grid-cols-2 grid-rows-1 gap-4"
          >
            <div class="col-span-5">
              <img
                :src="c.imagePath"
                :alt="c.name"
              />
            </div>
            <div class="col-span-7">
              <p class="text-sm">
                This course is part of <span class="font-bold text-sm">Summit's {{ c.name }} Certification Series.</span>
                <a
                  :href="'/certification-series/'+ c.url"
                  class="text-text-secondary font-bold text-sm"
                >
                  Learn More
                </a>
                about this an other certification series available exclusively to Summit Subscribers
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden md:block">
      <div
        v-if="ratings.rating && !courseTest"
        class="hidden md:block md:pb-4"
      >
        <div class="flex items-center">
          <l-rating-stars
            :rating="ratings.rating"
            class="text-accent-light"
          />
          <p class="ml-2">
            {{ ratings.rating }}/5
          </p>
        </div>
        <p class="font-bold text.sm">
          {{ ratings.ratingCount }} Ratings<span v-if="ratings.reviewCount > 0"> , <a href="#reviews"><span class="text-text-primary">{{ ratings.reviewCount }} Reviews</span></a></span>
        </p>
      </div>
      <course-ce-validator
        v-if="sessionCount > 0"
        :brochure-deadline="selectedSession.brochureDeadlineDate"
        :session="selectedSession"
        :ce-info="ceInfo"
        :user-state="userState"
        :user-profession="userProfession"
        :asha-review="ashaReview"
        :is-zero-hour="selectedSession.credits === 0"
        :format="selectedFormat"
        @update="updateCeInfo"
        @show-ce-tab="toggleCeTab"
        @scroll-to-tabs="scrollToTab('#info')"
      />
      <div v-if="!isBrochure">
        <div
          v-if="certSeries.length > 0"
          id="cert-info"
          class="py-6 pr-6"
        >
          <h5 class="leading-loose">
            Certification Series
          </h5>
          <div
            v-for="c in certSeries"
            :key="c.id"
            class="md:grid md:grid-flow-col md:grid-cols-2 md:grid-rows-1 md:gap-4"
          >
            <div class="col-span-5">
              <img
                :src="c.imagePath"
                :alt="c.name"
              />
            </div>
            <div class="col-span-7">
              <p class="text-sm">
                This course is part of <span class="font-bold text-sm">Summit's {{ c.name }} Certification Series.</span>
                <a
                  :href="'/certification-series/'+ c.url"
                  class="text-text-secondary font-bold text-sm"
                >
                  Learn More
                </a>
                about this an other certification series available exclusively to Summit Subscribers
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, } from 'pinia';
import PodcastSocialIcons from '../layouts/PodcastSocialIcons.vue';
import { useSearchStore, } from '../../stores/search';
import CourseCeValidator from '~/components/course/CeValidator.vue';
import { scrollToTab, } from '@/utils/reusedFunctions';

export default {
  name: 'TitlePreview',
  components: { PodcastSocialIcons, CourseCeValidator },
  props: {
    selectedSession: {
      type: Object,
      required: true,
    },
    userProfession: {
      type: Object,
      required: true,
    },
    userState: {
      type: Object,
      required: true,
    },
    ratings: {
      type: Object,
      required: true,
    },
    selectedFormat: {
      type: String,
      required: true,
    },
    certSeries: {
      type: Array,
      required: true,
    },
    isBrochure: {
      type: Boolean,
      required: true,
    },
    ashaReview: {
      type: Boolean,
      required: true,
    },
    ceInfo: {
      type: Object,
      required: true,
    },
    sessionCount: {
      type: Number,
      required: true,
    },
    masterCourseId: {
      type: String,
      required: true,
    },
    thumbnail: {
      type: String,
      required: true,
    },
    courseTitle: {
      type: String,
      required: true,
    },
    previewId: {
      type: String,
      default: null,
    },
    pathDetails: {
      type: Object,
      required: true,
      default: () => {},
    },
    courseTest: {
      type: Boolean,
      default: false,

    },
  },
  emits: ['update', 'show-ce-tab'],
  setup() {
    const router = useRouter();
    return { router };
  },
  computed: {
    ...mapState(useSearchStore, ['page']),
    returnTo() {
      // route is undefined if coming from a short link (email or /b)
      if (typeof this.pathDetails.route !== 'undefined' && typeof this.pathDetails.from !== 'undefined') {
        return this.pathDetails.route.query.type === 'brochure' ? this.pathDetails.from.path : '/courses';
      }
      else {
        return '/courses';
      }
    },
  },
  methods: {
    ...mapActions(useSearchStore, ['setPage']),
    scrollToTab,
    updateCeInfo(event) {
      this.$emit('update', event);
    },
    // in the CE validation component, there is a button to change the tab to be the CE tab.
    toggleCeTab() {
      this.$emit('show-ce-tab');
    },
    determineReturn() {
      this.setPage(this.page);
      this.router.push(this.returnTo);
    },
  },
};
</script>

<style scoped>
.course-test {
  visibility: hidden;
}
</style>
